import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import OffersService from '~/services/OffersService';
import PublicOffersService from '~/services/PublicOfferService';
import type { NearbyHotelOffer } from '~/services/SearchService';

const STALE_TIME = 5 * 60 * 1000;
const REGION = 'AU';

type NearbyHotelOfferImageSource = Pick<NearbyHotelOffer, 'offerId' | 'offerType'>;

export function useGetNearbyHotelOfferImages(offers: Array<NearbyHotelOfferImageSource>) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const uniqueOffers = useMemo(
    () => Array.from(new Map(offers.map((offer) => [offer.offerId, offer])).values()),
    [offers],
  );

  const queries = useQueries({
    queries: uniqueOffers.map((offer) => ({
      queryKey: ['search', 'nearby-hotel-offers', 'image', offer.offerId, offer.offerType, brand],
      queryFn: async (): Promise<{ offerId: string; imageId?: string }> => {
        if (offer.offerType === 'bedbank_hotel') {
          const response = await PublicOffersService.getBedbankOfferById(offer.offerId, REGION, brand);
          return {
            offerId: offer.offerId,
            imageId: response.result.images?.[0]?.id,
          };
        }

        const response = await OffersService.getOffer(offer.offerId);
        return {
          offerId: offer.offerId,
          imageId: response.result.images?.[0]?.id_cloudinary_external,
        };
      },
      enabled: !!brand,
      staleTime: STALE_TIME,
      retry: false,
    })),
  });

  const imageIdsByOfferId = useMemo(() => {
    const imageIds = new Map<string, string>();

    queries.forEach((query) => {
      if (query.data?.imageId) {
        imageIds.set(query.data.offerId, query.data.imageId);
      }
    });

    return imageIds;
  }, [queries]);

  return {
    imageIdsByOfferId,
    isLoading: queries.some((query) => query.isLoading),
  };
}
